import moment from 'moment'

export interface PersistedFilters {
	dateRange?: {
		from: string
		to: string
	}
	breadcrumbs?: {
		platforms?: string
		adAccounts?: string
		campaigns?: string
	}

	otherFiltersByPage?: Record<string, Record<string, string>>
}

const FILTER_STORAGE_KEY = 'clickguard_persisted_filters'
const VALID_FILTER_PAGES = [
	'dashboard',
	'analytics',
	'clicks',
	'threat',
	'exclusions',
	'blacklist',
	'reports',
	'protection'
]


const EXCLUDED_PARAMS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'ref',
	'source',
	'gclid',
	'fbclid',
	'tab' 
]

export function isAssetIndexPath(pathname: string): boolean {
	const parts = pathname.split('/').filter(Boolean)
	return (
		parts.length === 4 &&
		parts[0] === 'workspace' &&
		parts[2] === 'asset'
	)
}

export function isDashboardPath(pathname: string): boolean {
	return pathname.includes('/dashboard') || isAssetIndexPath(pathname)
}

export function isValidFilterPage(pathname: string): boolean {
	return (
		isAssetIndexPath(pathname) ||
		VALID_FILTER_PAGES.some((page) => pathname.includes(`/${page}`))
	)
}

export function isSettingsPage(pathname: string): boolean {
	return pathname.includes('/settings')
}

export function getFilterPageKey(pathname: string): string | null {
	if (isAssetIndexPath(pathname)) return 'dashboard'
	const match = VALID_FILTER_PAGES.find((p) => pathname.includes(`/${p}`))
	return match ?? null
}

export function getPersistedFiltersFromStorage(): PersistedFilters | null {
	try {
		const stored = sessionStorage.getItem(FILTER_STORAGE_KEY)
		if (!stored) return null
		const parsed = JSON.parse(stored) as PersistedFilters & {
			otherFilters?: Record<string, string>
		}
		if (parsed.otherFilters) delete parsed.otherFilters
		return parsed
	} catch (error) {
		console.error('Error reading persisted filters from storage:', error)
		return null
	}
}

export function saveFiltersToStorage(filters: PersistedFilters): void {
	try {
		sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters))
	} catch (error) {
		console.error('Error saving persisted filters to storage:', error)
	}
}

export function clearFiltersFromStorage(): void {
	try {
		sessionStorage.removeItem(FILTER_STORAGE_KEY)
	} catch (error) {
		console.error('Error clearing persisted filters from storage:', error)
	}
}

export function getFiltersFromURL(
	searchParams: URLSearchParams,
	pathname: string
): PersistedFilters {
	const filters: PersistedFilters = {}
	const from = searchParams.get('from')
	const to = searchParams.get('to')
	if (from && to) {
		filters.dateRange = { from, to }
	}

	const platforms = searchParams.get('platforms')
	const adAccounts = searchParams.get('adAccounts')
	const adAccount = searchParams.get('adAccount')
	const campaigns =
		searchParams.get('campaigns') ||
		searchParams.get('breadCampaigns') ||
		searchParams.get('breadCampaign') ||
		searchParams.get('campaign')

	if (platforms || adAccounts || adAccount || campaigns) {
		filters.breadcrumbs = {}
		if (platforms) filters.breadcrumbs.platforms = platforms

		if (adAccounts) {
			filters.breadcrumbs.adAccounts = adAccounts
		} else if (adAccount) {
			filters.breadcrumbs.adAccounts = adAccount
		}

		if (campaigns) {
			filters.breadcrumbs.campaigns = campaigns
		}
	}
	const otherFilters: Record<string, string> = {}
	searchParams.forEach((value, key) => {
		if (
			key === 'from' ||
			key === 'to' ||
			key === 'platforms' ||
			key === 'adAccounts' ||
			key === 'adAccount' ||
			key === 'campaigns' ||
			key === 'breadCampaigns' ||
			key === 'breadCampaign' ||
			key === 'campaign'
		) {
			return
		}

		if (EXCLUDED_PARAMS.includes(key)) {
			return
		}

		otherFilters[key] = value
	})

	if (Object.keys(otherFilters).length > 0) {
		const pageKey = getFilterPageKey(pathname)
		if (pageKey) {
			filters.otherFiltersByPage = { [pageKey]: otherFilters }
		}
	}

	return filters
}

export function syncFiltersToStorage(
	searchParams: URLSearchParams,
	pathname: string
): void {
	const urlFilters = getFiltersFromURL(searchParams, pathname)
	const existing = getPersistedFiltersFromStorage() || {}

	const pageKey = getFilterPageKey(pathname)
	const incomingPageFilters = pageKey
		? urlFilters.otherFiltersByPage?.[pageKey]
		: undefined

	const mergedOtherFiltersByPage = pageKey
		? {
			...existing.otherFiltersByPage,
			[pageKey]: incomingPageFilters ?? {}
		}
		: existing.otherFiltersByPage

	const hasBreadcrumbFilters =
		urlFilters.breadcrumbs && Object.keys(urlFilters.breadcrumbs).length > 0
	const hasDateRange = !!urlFilters.dateRange
	const hasPageFilters = incomingPageFilters && Object.keys(incomingPageFilters).length > 0
	if (!hasBreadcrumbFilters) {
		delete existing.breadcrumbs
	}
	if (!hasDateRange) {
		delete existing.dateRange
	}
	if (!hasPageFilters && pageKey && mergedOtherFiltersByPage) {
		delete mergedOtherFiltersByPage[pageKey]
	}

	if (hasBreadcrumbFilters || hasDateRange || hasPageFilters) {
		const merged: PersistedFilters = {
			...existing,
			breadcrumbs: hasBreadcrumbFilters
				? { ...existing.breadcrumbs, ...urlFilters.breadcrumbs }
				: existing.breadcrumbs,
			dateRange: urlFilters.dateRange || existing.dateRange,
			otherFiltersByPage: mergedOtherFiltersByPage
		}
		saveFiltersToStorage(merged)
	} else if (pageKey) {
		saveFiltersToStorage({
			...existing,
			otherFiltersByPage: mergedOtherFiltersByPage
		})
	}
}

export function convertFiltersForPage(
	searchParams: URLSearchParams,
	pathname: string
): URLSearchParams | null {
	if (!isValidFilterPage(pathname)) {
		return null
	}

	const newParams = new URLSearchParams(searchParams)
	let hasChanges = false

	// Normalize legacy keys to the new plural format owned by the breadcrumb.
	const adAccount = searchParams.get('adAccount')
	if (adAccount) {
		if (!searchParams.get('adAccounts')) newParams.set('adAccounts', adAccount)
		newParams.delete('adAccount')
		hasChanges = true
	}

	const legacyCampaign =
		searchParams.get('breadCampaigns') ||
		searchParams.get('breadCampaign') ||
		searchParams.get('campaign')
	if (legacyCampaign && !searchParams.get('campaigns')) {
		newParams.set('campaigns', legacyCampaign)
		hasChanges = true
	}
	if (searchParams.get('breadCampaigns')) {
		newParams.delete('breadCampaigns')
		hasChanges = true
	}
	if (searchParams.get('breadCampaign')) {
		newParams.delete('breadCampaign')
		hasChanges = true
	}
	if (searchParams.get('campaign')) {
		newParams.delete('campaign')
		hasChanges = true
	}

	return hasChanges ? newParams : null
}

export function restoreFiltersToURL(
	searchParams: URLSearchParams,
	pathname: string
): URLSearchParams | null {
	if (!isValidFilterPage(pathname)) {
		return null
	}
	const convertedParams = convertFiltersForPage(searchParams, pathname)
	if (convertedParams) {
		return convertedParams
	}
	const storedFilters = getPersistedFiltersFromStorage()
	if (!storedFilters || Object.keys(storedFilters).length === 0) {
		return null
	}
	const newParams = new URLSearchParams(searchParams)
	let hasChanges = false
	if (storedFilters.dateRange && (!newParams.get('from') || !newParams.get('to'))) {
		if (!newParams.get('from')) {
			newParams.set('from', storedFilters.dateRange.from)
			hasChanges = true
		}
		if (!newParams.get('to')) {
			newParams.set('to', storedFilters.dateRange.to)
			hasChanges = true
		}
	}
	
	if (storedFilters.breadcrumbs) {
		
		if (storedFilters.breadcrumbs.platforms && !newParams.get('platforms')) {
			newParams.set('platforms', storedFilters.breadcrumbs.platforms)
			hasChanges = true
		}
		
		const hasAdAccount = newParams.get('adAccounts')
		if (storedFilters.breadcrumbs.adAccounts && !hasAdAccount) {
			newParams.set('adAccounts', storedFilters.breadcrumbs.adAccounts)
			hasChanges = true
		}

		const hasCampaign = newParams.get('campaigns')
		if (storedFilters.breadcrumbs.campaigns && !hasCampaign) {
			newParams.set('campaigns', storedFilters.breadcrumbs.campaigns)
			hasChanges = true
		}
	}
	
	const pageKey = getFilterPageKey(pathname)
	const pageFilters = pageKey
		? storedFilters.otherFiltersByPage?.[pageKey]
		: undefined
	if (pageFilters) {
		Object.entries(pageFilters).forEach(([key, value]) => {
			if (!newParams.has(key)) {
				newParams.set(key, value)
				hasChanges = true
			}
		})
	}
	return hasChanges ? newParams : null
}

export function resetFilters(
	resetType: 'all' | 'breadcrumbs-only',
	searchParams: URLSearchParams
): URLSearchParams {
	const newParams = new URLSearchParams(searchParams)

	if (resetType === 'all') {
		
		newParams.delete('from')
		newParams.delete('to')
		newParams.delete('platforms')
		newParams.delete('adAccounts')
		newParams.delete('adAccount') 
		newParams.delete('breadCampaigns')
		newParams.delete('breadCampaign') 
		newParams.delete('campaign') 
		
		const stored = getPersistedFiltersFromStorage()
		if (stored?.otherFiltersByPage) {
			Object.values(stored.otherFiltersByPage).forEach((bucket) => {
				Object.keys(bucket).forEach((key) => newParams.delete(key))
			})
		}
		
		const filterKeysToRemove = [
			'ppc', 'status', 'converted', 'ip_address', 'placement', 'device_id',
			'campaign_type', 'country', 'bounced', 'vpn', 'threat_level',
			'bot_probability', 'user_interaction', 'ip_usage', 'identifier', 'session_id', 'labels',
			'type', 'entity', 'reason', 'expiration_date', 'scope', 'campaigns'
		]
		filterKeysToRemove.forEach((key) => {
			newParams.delete(key)
		})

		clearFiltersFromStorage()
	} else if (resetType === 'breadcrumbs-only') {

		newParams.delete('platforms')
		newParams.delete('adAccounts')
		newParams.delete('adAccount')
		newParams.delete('campaigns')
		newParams.delete('breadCampaigns')
		newParams.delete('breadCampaign')
		newParams.delete('campaign')

		const stored = getPersistedFiltersFromStorage()
		if (stored) {
			saveFiltersToStorage({
				dateRange: stored.dateRange,
				otherFiltersByPage: stored.otherFiltersByPage
			})
		}
	}
	return newParams
}

export function getDefaultDateRange(): { from: Date; to: Date } {
	return {
		from: moment().subtract(30, 'days').startOf('day').toDate(),
		to: moment().endOf('day').toDate()
	}
}
